import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';
import { authApi } from '../utils/api';
import { Panel } from '../components/ledger';

// A share link's join page sets this before Yahoo consent. It is read once and removed,
// so a failed or abandoned attempt never redirects a later link.
function takePendingJoin() {
  try {
    const pending = window.sessionStorage.getItem('iceiq-pending-join');
    window.sessionStorage.removeItem('iceiq-pending-join');
    return pending;
  } catch {
    return null;
  }
}

export function OAuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('Processing...');
  const isProcessing = useRef(false);

  useEffect(() => {
    const handleCallback = async () => {
      // Prevent double-submission (React StrictMode can trigger effects twice)
      if (isProcessing.current) return;
      isProcessing.current = true;
      const pendingJoin = takePendingJoin();

      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const errorParam = searchParams.get('error');

      if (errorParam) { setError(`Yahoo authorization failed: ${errorParam}`); return; }
      if (!code) { setError('Missing authorization code'); return; }

      // Validate state matches (CSRF protection). No saved state usually means this is a
      // stale OAuth redirect replayed via the browser back button after a prior successful
      // link already cleared it — bounce home quietly instead of raising a false CSRF alarm.
      const savedState = localStorage.getItem('yahoo_oauth_state');
      if (!savedState) { navigate('/settings', { replace: true }); return; }
      if (state !== savedState) { setError('Invalid state parameter — possible CSRF attack'); return; }

      try {
        setStatus('Linking Yahoo account...');
        const token = await getToken();
        if (!token) {
          setError('Your session expired during the Yahoo authorization flow. Please sign in again and try connecting Yahoo.');
          return;
        }
        const result = await authApi.handleCallback(code, state, token);

        if (result.success && result.data?.linked) {
          localStorage.removeItem('yahoo_oauth_state');
          setStatus('Linked! Redirecting...');
          navigate(pendingJoin ? `/join/${encodeURIComponent(pendingJoin)}` : '/settings', { replace: true });
        } else {
          setError(result.error || 'Failed to link Yahoo account');
        }
      } catch (err) {
        setError(err.message || 'An error occurred during linking');
      }
    };

    handleCallback();
  }, [searchParams, navigate, getToken]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--ledger-bg)' }}>
        <Panel bodyClassName="p-8 text-center" className="max-w-md w-full">
          <AlertCircle className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--ledger-bad)' }} />
          <h1 className="text-xl font-bold mb-2" style={{ color: 'var(--ledger-ink)' }}>Linking Failed</h1>
          <p className="mb-6" style={{ color: 'var(--ledger-muted)' }}>{error}</p>
          <button
            onClick={() => navigate('/settings', { replace: true })}
            className="ledger-btn"
            style={{ background: 'var(--ledger-ink)', borderColor: 'var(--ledger-ink)', color: 'var(--ledger-panel)' }}
          >
            Back to Settings
          </button>
        </Panel>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--ledger-bg)' }}>
      <Panel bodyClassName="p-8 text-center" className="max-w-md w-full">
        <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin" style={{ color: 'var(--ledger-muted)' }} />
        <h1 className="text-xl font-bold mb-2" style={{ color: 'var(--ledger-ink)' }}>Linking Yahoo</h1>
        <p style={{ color: 'var(--ledger-muted)' }}>{status}</p>
      </Panel>
    </div>
  );
}

export default OAuthCallback;
